import axios, {
	AxiosError,
	AxiosRequestHeaders,
	AxiosResponse,
	InternalAxiosRequestConfig,
} from 'axios';
import { Configuration, PostConfiguration } from 'data/configuration';
import { AgentStates } from 'types';
import { ClaimRewardRequest, ClaimWindow } from 'types/claim';
import { AccountType, DistributionEvent } from 'types/distribution';
import { store } from 'store';
import { JENIUS_BASE_URL, VCRED_BASE_URL } from 'data/constant';

interface ServerStatusType {
	status: 'healthy';
}

export interface TradesType {
	asset: string;
	createdAt: string;
	id: string;
	market_type: string;
	pnl: number;
	price: number;
	side: 'buy' | 'sell';
	size: number;
}

interface ChatResponseType {
	processing_time: string;
	query: string;
	response: string;
	success: boolean;
	thread_id: string;
}

interface StoreChatHistoryType {
	wallet_address: string;
	query: string;
	thread_id?: string;
	sync_trades?: boolean;
	sync_account?: boolean;
}

export interface CreateConfirmUserType {
	ok: true;
	user_id: '0x17dA76E5BCc34170BCd55bc6289bAE18e62c49d9';
}

interface AppendConversationType {
	wallet_address: string;
	query: string;
	response: string;
}

interface DepositRequest {
	contract: string;
	address: string;
	token?: string;
	amount: string;
	domain?: string;
	eventType: 'withdraw' | 'deposit' | 'claim';
}

interface DepositResponse {
	success: boolean;
	message: string;
}

interface AgentConfig {
	address: string;
	agent: string;
	config: {
		max_amount_per_trade: number;
		total_trade_amount: number;
		take_profit_per_trade: number;
		stop_loss_per_trade: number;
	};
	status: string;
	createdAt: string;
	updatedAt: string;
	id: string;
}

const vcredApiClient = axios.create({
	baseURL: VCRED_BASE_URL,
	headers: {
		'Content-Type': 'application/json',
	},
});

const apiClient = axios.create({
	baseURL: JENIUS_BASE_URL,
	headers: {
		'Content-Type': 'application/json',
	},
});

export interface UserDepositParams {
	contract: string;
	address: string;
	token?: string;
	amount?: string;
}

interface UserDepositResponse {
	address: string;
	amount: string;
	contract: string;
	createdAt: string;
	id: string;
	updatedAt: string;
}

export interface UserWithdrawDeposit {
	contract: string;
	address: string;
	amount: string;
	token: string;
	eventType: 'withdraw' | 'deposit' | 'claim';
	claimTimePeriod: string;
	claimStatus?: 'processing' | 'processed';
	createdAt?: string;
	domain?: string;
}

export const userDeposit = async (
	depositData: DepositRequest,
): Promise<DepositResponse> => {
	try {
		const response: AxiosResponse<DepositResponse> = await apiClient.post(
			'/api/user/transaction',
			{ ...depositData, domain: window.location.host },
		);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message ||
				'An error occurred while processing your request.',
		);
	}
};

export const getUserDeposit = async (
	params: UserDepositParams,
): Promise<any> => {
	try {
		const response = await apiClient.get<{
			lastDeposit: UserDepositResponse[];
		}>('/api/user/last-deposit', { params });
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message || 'Error fetching user deposit data',
		);
	}
};

/*export const getUserEarned = async (address: string): Promise<any> => {
	try {
		const response = await apiClient.get<{
			lastDeposit: UserDepositResponse[];
		}>(`/api/user/dudu/total-earning?user_wallet_addrs=${address}`);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message || 'Error fetching user deposit data',
		);
	}
};*/

/*export const getTotalWallet = async (): Promise<any> => {
	try {
		const response = await apiClient.get<{
			lastDeposit: UserDepositResponse[];
		}>('/api/stats/users-summary');
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message || 'Error fetching user deposit data',
		);
	}
};*/

/*export const userWithdrawTransaction = async (
	payload: UserWithdrawDeposit,
): Promise<any> => {
	try {
		const response = await apiClient.post<{
			lastDeposit: UserDepositResponse[];
		}>('/api/user/transaction', {
			...payload,
			domain: window.location.host,
		});
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message || 'Error fetching user deposit data',
		);
	}
};*/

export const getClaimList = async (contract: string, address: string) => {
	try {
		const response: AxiosResponse<UserWithdrawDeposit[]> =
			await apiClient.get(
				`/api/user/claims?address=${address}&contract=${contract}&domain=${window.location.host}`,
			);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message || 'Error fetching user deposit data',
		);
	}
};

export const getConfiguration = async (address: string) => {
	try {
		const response: AxiosResponse<
			{
				address: `0x${string}`;
				agent: string;
				config: Configuration;
				isStarted: false;
				status: AgentStates;
				createdAt: string;
				updatedAt: string;
				id: string;
			}[]
		> = await apiClient.get(`/api/agents?addresses=${address}`);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message || 'Error fetching user deposit data',
		);
	}
};

export const saveConfiguration = async (value: PostConfiguration) => {
	try {
		const response: AxiosResponse<Configuration> =
			await vcredApiClient.post(`/api/agents/config`, value);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data.errors || 'Error fetching user deposit data',
		);
	}
};

export const startAgent = async (agentId: string, address: string) => {
	try {
		const response: AxiosResponse<Configuration> =
			await vcredApiClient.post(`/api/agents/${agentId}/start`, {
				address,
			});
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message || 'Error fetching user deposit data',
		);
	}
};

export const stopAgent = async (agentId: string, address: string) => {
	try {
		const response: AxiosResponse<Configuration> =
			await vcredApiClient.post(`/api/agents/${agentId}/request-stop`, {
				address,
			});
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message || 'Error fetching user deposit data',
		);
	}
};

export const getAgentsByAddresses = async (
	addresses: string[],
): Promise<AgentConfig[]> => {
	try {
		const query = addresses
			.map((addr) => `addresses[]=${encodeURIComponent(addr)}`)
			.join('&');
		const response: AxiosResponse<AgentConfig[]> = await vcredApiClient.get(
			`/api/agents?${query}`,
		);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message ||
				'Error fetching agent configuration data',
		);
	}
};

export const getClaimWindow = async (agent: string) => {
	try {
		const response: AxiosResponse<ClaimWindow> = await apiClient.get(
			`/api/reward/claim-window`,
			{
				params: { agent },
			},
		);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message || 'Error fetching user deposit data',
		);
	}
};

export const createNewClaimReward = async (payload: ClaimRewardRequest) => {
	try {
		const response: AxiosResponse<ClaimWindow> = await apiClient.post(
			`/api/reward/claim`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message || 'Error fetching user deposit data',
		);
	}
};

export const listOfAllRewardRequest = async (
	payload: Pick<ClaimRewardRequest, 'domain' | 'contract' | 'address'>,
) => {
	try {
		const response: AxiosResponse<ClaimRewardRequest[]> =
			await apiClient.get(`/api/reward/user-claims`, { params: payload });
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message || 'Error fetching user deposit data',
		);
	}
};

export const listOfAllDistribution = async (
	payload: Pick<ClaimRewardRequest, 'contract' | 'address'>,
) => {
	try {
		const response: AxiosResponse<DistributionEvent[]> =
			await apiClient.get(`/api/reward/distribution`, {
				params: payload,
			});
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message || 'Error fetching user deposit data',
		);
	}
};

export const getUserAccountInformation = async (
	payload: Pick<ClaimRewardRequest, 'contract' | 'address'>,
) => {
	try {
		const response: AxiosResponse<AccountType> = await apiClient.get(
			`/api/reward/account`,
			{
				params: payload,
			},
		);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message || 'Error fetching user deposit data',
		);
	}
};

/*export const getUserDetails = async () => {
	try {
		const response: AxiosResponse<ClaimRewardRequest[]> = await axios.get(
			`https://api.parsec.finance/graphql`,
			{
				params: {
					operationName: 'GetTxs',
					variables: {
						addresses: [
							'0x271c39c9526e89b5973dcfda6f81c37fc7243990',
						],
						limit: 25,
						offset: 0,
						chains: ['hyper_evm'],
					},
					query: 'query GetTxs($addresses: [String!], $limit: Int!, $chains: [String], $offset: Int, $call: String, $to: String, $from: String, $since: Int, $before: Int) {\n  txs(\n    addresses: $addresses\n    limit: $limit\n    chains: $chains\n    offset: $offset\n    functionCall: $call\n    to: $to\n    from: $from\n    since: $since\n    before: $before\n  ) {\n    chain\n    block\n    hash\n    to\n    toAddressLabel {\n      label\n      src\n      address\n      tags\n      chains\n      imgSrc\n      asset {\n        id\n        address\n        symbol\n        imgSrc\n        chains\n        __typename\n      }\n      __typename\n    }\n    from\n    fromAddressLabel {\n      label\n      src\n      address\n      tags\n      chains\n      imgSrc\n      asset {\n        id\n        address\n        symbol\n        imgSrc\n        chains\n        __typename\n      }\n      __typename\n    }\n    gasPrice\n    gasUsed\n    input\n    call\n    status\n    index\n    value\n    timestamp\n    meta\n    contractCreation\n    marketData {\n      type\n      data\n      __typename\n    }\n    inputDecoded {\n      function\n      args\n      __typename\n    }\n    message\n    __typename\n  }\n}',
				},
			},
		);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message || 'Error fetching user deposit data',
		);
	}
};*/

export interface AuthNonceResponse {
	current: number;
	timestamp: string;
	authMessage: string;
}

export interface IAuthSignUpLogin {
	message: string;
	signature: string;
	referral_code?: string;
}

export interface IAuthSignUpLoginResponse {
	address: string;
	token: string;
}

export interface IChatThreadResponse {
	thread_id: string;
	query: string;
	response: string;
	success?: boolean;
	processing_time: string;
}

apiClient.interceptors.request.use(
	(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
		const token = store.getState().auth.loginResponse.token;
		//const wallet = store.getState().auth.loginResponse.address;

		// Ensure headers is always an object
		config.headers = (config.headers ?? {}) as AxiosRequestHeaders;

		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
			//config.headers.WALLET = wallet;
		}

		return config;
	},
	(error: AxiosError) => Promise.reject(error),
);

// latest apis
export const userProfileRecentConversation = async (
	userAddress: string,
): Promise<AuthNonceResponse> => {
	try {
		const response: AxiosResponse<AuthNonceResponse> = await apiClient.get(
			`/user/WALLET=${userAddress}`,
		);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message ||
				'An error occurred while processing your request.',
		);
	}
};

export const serverHealth = async (): Promise<ServerStatusType> => {
	try {
		const response: AxiosResponse<ServerStatusType> = await apiClient.get(
			`/health`,
		);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message ||
				'An error occurred while processing your request.',
		);
	}
};

export const statisticsUser = async (): Promise<ServerStatusType> => {
	try {
		const response: AxiosResponse<ServerStatusType> = await apiClient.get(
			`/users/stats`,
		);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message ||
				'An error occurred while processing your request.',
		);
	}
};

export const userConversation = async (
	WALLET_ADDRESS: string,
): Promise<ServerStatusType> => {
	try {
		const response: AxiosResponse<ServerStatusType> = await apiClient.get(
			`/conversations?wallet_address=${WALLET_ADDRESS}`,
		);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message ||
				'An error occurred while processing your request.',
		);
	}
};

export const allTrades = async (
	WALLET_ADDRESS: string,
): Promise<TradesType[]> => {
	try {
		const response: AxiosResponse<TradesType[]> = await apiClient.get(
			`/trades?wallet_address=${WALLET_ADDRESS}`,
		);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message ||
				'An error occurred while processing your request.',
		);
	}
};

export const fiftyTrades = async (
	WALLET_ADDRESS: string,
): Promise<ServerStatusType> => {
	try {
		const response: AxiosResponse<ServerStatusType> = await apiClient.get(
			`/trades?wallet_address=${WALLET_ADDRESS}&limit=50`,
		);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message ||
				'An error occurred while processing your request.',
		);
	}
};

export const tradeSummary = async (
	WALLET_ADDRESS: string,
): Promise<ServerStatusType> => {
	try {
		const response: AxiosResponse<ServerStatusType> = await apiClient.get(
			`/trades/${WALLET_ADDRESS}/summary`,
		);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message ||
				'An error occurred while processing your request.',
		);
	}
};

export const accountSnapshot = async (
	WALLET_ADDRESS: string,
): Promise<ServerStatusType> => {
	try {
		const response: AxiosResponse<ServerStatusType> = await apiClient.get(
			`/account?wallet_address=${WALLET_ADDRESS}`,
		);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message ||
				'An error occurred while processing your request.',
		);
	}
};

export const userPortfolio = async (
	WALLET_ADDRESS: string,
): Promise<ServerStatusType> => {
	try {
		const response: AxiosResponse<ServerStatusType> = await apiClient.get(
			`/portfolio?wallet_address=${WALLET_ADDRESS}`,
		);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message ||
				'An error occurred while processing your request.',
		);
	}
};

export const storeChatEntry = async (
	payload: StoreChatHistoryType,
): Promise<ChatResponseType> => {
	try {
		const response: AxiosResponse<ChatResponseType> = await apiClient.post(
			`/chat`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message ||
				'An error occurred while processing your request.',
		);
	}
};

export const createOrConfirmUser = async (
	wallet_address: string,
): Promise<CreateConfirmUserType> => {
	try {
		const response: AxiosResponse<CreateConfirmUserType> =
			await apiClient.post(`/users`, { wallet_address });
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message ||
				'An error occurred while processing your request.',
		);
	}
};

export const appendConversation = async (
	payload: AppendConversationType,
): Promise<AppendConversationType> => {
	try {
		const response: AxiosResponse<AppendConversationType> =
			await apiClient.post(`/conversations`, payload);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message ||
				'An error occurred while processing your request.',
		);
	}
};

export const fetchEntireTradeHistory = async (
	wallet_address: string,
): Promise<AppendConversationType> => {
	try {
		const response: AxiosResponse<AppendConversationType> =
			await apiClient.post(`/trades/sync`, { wallet_address });
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message ||
				'An error occurred while processing your request.',
		);
	}
};

export const syncAccount = async (
	wallet_address: string,
): Promise<AppendConversationType> => {
	try {
		const response: AxiosResponse<AppendConversationType> =
			await apiClient.post(`/account/sync`, { wallet_address });
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message ||
				'An error occurred while processing your request.',
		);
	}
};

/*





































*/

export const getAuthNonce = async (
	userAddress: string,
): Promise<AuthNonceResponse> => {
	try {
		const response: AxiosResponse<AuthNonceResponse> = await apiClient.get(
			`/api/jenius/auth/nonce?address=${userAddress}`,
		);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message ||
				'An error occurred while processing your request.',
		);
	}
};

export const authSignUpLogin = async (
	payload: IAuthSignUpLogin,
): Promise<IAuthSignUpLoginResponse> => {
	try {
		const response: AxiosResponse<IAuthSignUpLoginResponse> =
			await apiClient.post('/api/jenius/auth/wallet', { ...payload });
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message ||
				'An error occurred while processing your request.',
		);
	}
};

/*export const getCurrentUser = async (
	userId: string,
	token: string,
): Promise<Omit<IAuthSignUpLoginResponse, 'token'>> => {
	try {
		const response = await apiClient.get<
			Omit<IAuthSignUpLoginResponse, 'token'>
		>('/api/jenius/current-user', {
			headers: {
				id: userId,
				Authorization: `Bearer ${token}`,
			},
		});
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message ??
				'An error occurred while fetching the current user.',
		);
	}
};*/

export const getChatThread = async (
	query: string,
	threadId?: string,
): Promise<IChatThreadResponse> => {
	try {
		const payload: { query: string; threadId?: string } = { query };
		if (threadId) {
			payload.threadId = threadId;
		}

		const response = await apiClient.post<IChatThreadResponse>(
			'/api/jenius/chat/query',
			payload,
		);

		return response.data;
	} catch (error: any) {
		const message =
			error.response?.data?.message ??
			'An error occurred while fetching the chat thread.';
		throw new Error(message);
	}
};
